import { HttpEvent, HttpEventType } from '@angular/common/http';
import { filter, MonoTypeOperatorFunction, tap } from 'rxjs';
import { IncidentAttachmentRequest, IncidentAttachmentUpload } from '../models/accidents-and-incidents-request.model';

const COMPLETE_PROGRESS = 100;

// Progress stops short of complete until the server confirms the file was stored
const MAX_IN_FLIGHT_PROGRESS = 95;

const FALLBACK_MEDIA_TYPE = 'application/octet-stream';

// Browsers leave File.type empty for some formats (e.g. HEIC on Windows), so the extension decides instead
const MEDIA_TYPES_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.heic': 'image/heic',
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
  '.webm': 'video/webm'
};

export function getFileExtension(fileName: string): string | null {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.substring(dotIndex) : null;
}

export function getMediaType(file: File): string {
  const extension = getFileExtension(file.name)?.toLowerCase() ?? '';
  return file.type || MEDIA_TYPES_BY_EXTENSION[extension] || FALLBACK_MEDIA_TYPE;
}

export function createAttachmentUpload(file: File): IncidentAttachmentUpload {
  return {
    file,
    size: file.size,
    name: file.name,
    reference: crypto.randomUUID(),
    extension: getFileExtension(file.name),
    mediaType: getMediaType(file),
    isUploading: true,
    uploadProgress: 0,
    uploadSuccess: false,
    uploadFailed: false
  };
}

export function isDuplicateAttachment(uploads: IncidentAttachmentUpload[], file: File): boolean {
  return uploads.some((upload) => upload.name === file.name && upload.size === file.size);
}

export function areUploadsComplete(uploads: IncidentAttachmentUpload[]): boolean {
  return uploads.every((upload) => upload.uploadSuccess && !upload.isUploading && !upload.uploadFailed);
}

export function toAttachmentRequests(uploads: IncidentAttachmentUpload[]): IncidentAttachmentRequest[] {
  return uploads.map(({ name, reference, extension, mediaType }) => ({ name, reference, extension, mediaType }));
}

export function buildTempAttachmentFormData(assetId: number, upload: IncidentAttachmentUpload): FormData {
  const formData = new FormData();
  formData.append('AssetId', assetId.toString());
  formData.append('Name', upload.name);
  formData.append('Reference', upload.reference);
  formData.append('Extension', upload.extension ?? '');
  formData.append('MediaType', upload.mediaType);
  formData.append('Attachment', upload.file, upload.name);
  return formData;
}

// Writes upload progress onto the attachment and lets only the final server response through
export function trackUploadProgress<T>(upload: IncidentAttachmentUpload): MonoTypeOperatorFunction<HttpEvent<T>> {
  return (source) => source.pipe(
    tap((event) => {
      if (event.type === HttpEventType.UploadProgress) {
        const total = event.total ?? upload.size;
        const progress = total > 0 ? Math.round((event.loaded / total) * COMPLETE_PROGRESS) : 0;
        upload.uploadProgress = Math.min(progress, MAX_IN_FLIGHT_PROGRESS);
      }
    }),
    filter((event) => event.type === HttpEventType.Response)
  );
}

export function markUploadSucceeded(upload: IncidentAttachmentUpload): void {
  upload.uploadProgress = COMPLETE_PROGRESS;
  upload.uploadSuccess = true;
  upload.uploadFailed = false;
  upload.isUploading = false;
}

export function markUploadFailed(upload: IncidentAttachmentUpload): void {
  upload.uploadSuccess = false;
  upload.uploadFailed = true;
  upload.isUploading = false;
}
